import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';

const articleImageMap: Record<string, string> = {
  'article-newbie-01': 'https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20flat%20lay%20of%20a%20smartphone%20displaying%20photo%20editing%20app%20interface%20on%20clean%20desk%20with%20soft%20natural%20light%2C%20creative%20workspace%20vibe%2C%20editorial%20photography%20style%20with%20gentle%20shadows%20and%20creamy%20beige%20background&width=800&height=480&seq=article-newbie-01&orientation=landscape',
  'article-update-02': 'https://readdy.ai/api/search-image?query=A%20modern%20smartphone%20floating%20with%20glowing%20AI%20circuit%20patterns%20around%20it%20on%20a%20warm%20gradient%20background%20of%20soft%20yellow%20and%20cream%20tones%2C%20futuristic%20yet%20approachable%20tech%20aesthetic%2C%20clean%20composition%20with%20subtle%20shadows&width=800&height=480&seq=article-update-02&orientation=landscape',
  'article-filter-03': 'https://readdy.ai/api/search-image?query=A%20beautiful%20flat%20lay%20of%20color%20palette%20swatches%20and%20a%20phone%20showing%20photo%20editing%20app%20on%20a%20warm%20cream%20desk%20surface%2C%20creative%20studio%20atmosphere%20with%20soft%20natural%20side%20lighting%2C%20artistic%20composition&width=800&height=480&seq=article-filter-03&orientation=landscape',
  'article-compare-04': 'https://readdy.ai/api/search-image?query=A%20side%20by%20side%20comparison%20of%20two%20smartphones%20displaying%20different%20photo%20editing%20app%20results%20on%20a%20clean%20minimalist%20desk%2C%20warm%20natural%20lighting%2C%20editorial%20product%20comparison%20style%20with%20soft%20shadows&width=800&height=480&seq=article-compare-04&orientation=landscape',
  'article-typography-05': 'https://readdy.ai/api/search-image?query=A%20creative%20flat%20lay%20of%20typography%20samples%20and%20a%20phone%20displaying%20text%20layout%20design%20on%20a%20warm%20beige%20background%2C%20editorial%20design%20vibe%20with%20soft%20natural%20light%2C%20clean%20and%20artistic%20composition&width=800&height=480&seq=article-typography-05&orientation=landscape',
  'article-android-06': 'https://readdy.ai/api/search-image?query=An%20Android%20smartphone%20displaying%20app%20download%20page%20on%20screen%20placed%20on%20a%20warm%20wooden%20desk%20with%20soft%20natural%20light%2C%20clean%20minimalist%20tech%20lifestyle%20photography%2C%20creamy%20background%20tones&width=800&height=480&seq=article-android-06&orientation=landscape',
  'article-template-07': 'https://readdy.ai/api/search-image?query=A%20creative%20workspace%20with%20a%20phone%20showing%20design%20template%20layout%2C%20surrounded%20by%20design%20sketches%20and%20color%20swatches%20on%20a%20warm%20cream%20desk%2C%20artistic%20studio%20atmosphere%20with%20soft%20morning%20light%2C%20clean%20composition&width=800&height=480&seq=article-template-07&orientation=landscape',
  'article-faq-08': 'https://readdy.ai/api/search-image?query=A%20minimalist%20flat%20lay%20of%20a%20phone%20displaying%20help%20and%20support%20screen%20with%20a%20small%20plant%20and%20coffee%20cup%20on%20a%20warm%20beige%20desk%2C%20soft%20natural%20light%20from%20window%2C%20calm%20and%20helpful%20atmosphere&width=800&height=480&seq=article-faq-08&orientation=landscape',
};

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!slug) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-16 text-center">
        <p className="text-foreground-500">文章不存在</p>
      </div>
    );
  }

  const article = getArticleBySlug(slug);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-16 text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
          <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
        </div>
        <p className="text-foreground-500 text-sm mb-4">文章未找到</p>
        <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
          返回首页
        </Link>
      </div>
    );
  }

  const categoryInfo = getCategoryBySlug(article.category);
  const relatedArticles = getRelatedArticles(slug, article.category, 3);

  return (
    <>
      <SEOHead
        title={`${article.title} - 黄油下载`}
        description={article.excerpt}
        keywords={article.tags.join(',') + ',黄油下载,黄油相机'}
        canonicalUrl={`https://huangyouxiazai.com/article/${slug}`}
        ogType="article"
      />

      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.date,
          url: `https://huangyouxiazai.com/article/${slug}`,
        }}
      />

      <article className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: categoryInfo?.name || '分类', path: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <header className="mt-4 mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                {tag}
              </span>
            ))}
            <span className="text-xs text-foreground-400">{article.date}</span>
            <span className="text-xs text-foreground-400">·</span>
            <span className="text-xs text-foreground-400">{article.readTime}阅读</span>
          </div>

          <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 leading-snug mb-4">
            {article.title}
          </h1>

          <div className="flex items-center gap-3 pb-6 border-b border-background-200">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-full">
              <i className="ri-user-line text-primary-600 text-sm"></i>
            </div>
            <span className="text-sm text-foreground-600">{article.author}</span>
          </div>
        </header>

        <div
          className="prose-custom"
          dangerouslySetInnerHTML={{
            __html: article.content
              .replace(/<h2>/g, '<h2 class="text-xl font-heading text-foreground-900 mt-8 mb-4">')
              .replace(/<p>/g, '<p class="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">'),
          }}
        />

        {relatedArticles.length > 0 && (
          <section className="mt-14 pt-8 border-t border-background-200">
            <h3 className="text-lg font-heading text-foreground-900 mb-5">相关文章推荐</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/article/${rel.slug}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 p-4 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                    {rel.title}
                  </h4>
                  <p className="text-xs text-foreground-500 line-clamp-2">{rel.excerpt}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </>
  );
}